import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, MapPin } from 'lucide-react';

/**
 * LocationModal - Modal for creating a new location with coordinates
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {Array} areas - List of available areas
 * @param {string} areaId - Selected area ID
 * @param {function} onAreaIdChange - Callback when area changes
 * @param {string} letter - Location letter (e.g., "M")
 * @param {function} onLetterChange - Callback when letter changes
 * @param {string} name - Location name
 * @param {function} onNameChange - Callback when name changes
 * @param {string} address - Location address (optional)
 * @param {function} onAddressChange - Callback when address changes
 * @param {string} latitude - Latitude coordinate
 * @param {function} onLatitudeChange - Callback when latitude changes
 * @param {string} longitude - Longitude coordinate
 * @param {function} onLongitudeChange - Callback when longitude changes
 * @param {function} onCreateClick - Callback when "Opprett lokalitet" is clicked
 * @param {boolean} loading - Loading state
 */
export default function LocationModal({
  isOpen,
  onClose,
  areas,
  areaId,
  onAreaIdChange,
  letter,
  onLetterChange,
  name,
  onNameChange,
  address,
  onAddressChange,
  latitude,
  onLatitudeChange,
  longitude,
  onLongitudeChange,
  onCreateClick,
  loading = false
}) {
  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={onClose}
    >
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-teal-700">
            <MapPin className="w-6 h-6" />
            <span className="text-xl">Opprett ny lokalitet</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Area selection */}
          <div className="space-y-2">
            <Label htmlFor="location-area" className="text-sm font-semibold">
              Område <span className="text-red-500">*</span>
            </Label>
            <Select value={areaId} onValueChange={onAreaIdChange}>
              <SelectTrigger className="h-12 border-2">
                <SelectValue placeholder="Velg område..." />
              </SelectTrigger>
              <SelectContent>
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Letter */}
            <div className="space-y-2">
              <Label htmlFor="location-letter" className="text-sm font-semibold">
                Bokstav <span className="text-red-500">*</span>
              </Label>
              <Input
                id="location-letter"
                value={letter}
                onChange={(e) => onLetterChange(e.target.value.toUpperCase())}
                placeholder="M"
                maxLength={1}
                className="h-12 text-center text-xl font-bold uppercase border-2"
              />
            </div>
            
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="location-name" className="text-sm font-semibold">
                Navn <span className="text-red-500">*</span>
              </Label>
              <Input
                id="location-name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Skarsfj., Bokna..."
                className="h-12 border-2"
              />
            </div>
          </div>
          
          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="location-address" className="text-sm font-semibold">
              Adresse (valgfritt)
            </Label>
            <Input
              id="location-address"
              value={address}
              onChange={(e) => onAddressChange(e.target.value)}
              placeholder="F.eks. Tysvær, Rogaland"
              className="h-12 border-2"
            />
            <p className="text-xs text-slate-500">
              Du kan også søke opp adresse på kart eller klikke på kartet for å velge posisjon
            </p>
          </div>
          
          {/* Coordinates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location-latitude" className="text-sm font-semibold">
                Breddegrad (Latitude)
              </Label>
              <Input
                id="location-latitude"
                type="number"
                step="0.000001"
                value={latitude}
                onChange={(e) => onLatitudeChange(e.target.value)}
                placeholder="59.25"
                className="h-12 border-2"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location-longitude" className="text-sm font-semibold">
                Lengdegrad (Longitude)
              </Label>
              <Input
                id="location-longitude"
                type="number"
                step="0.000001"
                value={longitude}
                onChange={(e) => onLongitudeChange(e.target.value)}
                placeholder="5.80"
                className="h-12 border-2"
              />
            </div>
          </div>
          
          {/* Preview */}
          {letter && name && (
            <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-300 rounded-xl">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{letter.toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-teal-700 mb-1">Forhåndsvisning</p>
                  <p className="text-base font-bold text-slate-900">
                    Lokalitet {letter.toUpperCase()} - {name}
                  </p>
                  {address && (
                    <p className="text-sm text-slate-600 mt-1">📍 {address}</p>
                  )}
                  {latitude && longitude && (
                    <p className="text-xs text-slate-500 mt-1">
                      Koordinater: {latitude}, {longitude}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Info box */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Tips:</strong> Lokaliteten vil bli tilgjengelig i skjemaet umiddelbart etter opprettelse. 
              Du kan også administrere lokaliteter via &quot;Lokaliteter&quot; menyen.
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex space-x-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            className="px-6"
          >
            Avbryt
          </Button>
          <Button
            onClick={onCreateClick}
            disabled={!letter.trim() || !name.trim() || !areaId || loading}
            className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 px-6"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Oppretter...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Opprett lokalitet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
